import React, { FC, useEffect, useState } from 'react';

import { Box, Grid, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  generatedProfessionalInvoice,
  generateProfessionalInvoice,
  professionalActions,
  professionalInvoice,
  resetProfessionalValue,
  resetStatus,
  successSelector,
} from '@pharmaplan/common';
import en from '@pharmaplan/common/localization/en';
import fr from '@pharmaplan/common/localization/fr';

import DownloadPDF from '../common/DownloadPDF';
import CustomButton from '../common/CustomButton';
import CustomCheckbox from '../common/CustomCheckbox';
import { useAppSelector } from '../../hooks/useAppSelector';
import useInvoicesCalculation from '../../hooks/Invoice/useInvoiceCalculation';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { MomentTimeFormats } from '../../helpers/Constants';
import { downloadPDF } from '../../helpers/Functions';
import strings from '../../localization';

import { getDayMonthDateYear } from '../Reports/helpers';
import { showSuccess } from '../Admin/Profile/Pharmacist/PharmacistViewProfile/helper';

import { tableHeader, tableRow } from './InvoiceTable/helper';
import InvoiceBox from './InvoiceBox';
import InvoiceTable from './InvoiceTable';
import {
  professionalConfig,
  clientConfig,
  bankConfig,
  invoiceConfig,
} from './helper';
import { IInvoiceStateBookings } from './InvoiceLayout/InvoiceLayout';
import styles from './styles';

interface IInvoice {
  setInvoiceState: React.Dispatch<
    React.SetStateAction<Record<string, IInvoiceStateBookings>>
  >;
  invoiceState: Record<string, IInvoiceStateBookings>;
}

const successAction = professionalActions.generateProfessionalInvoice;

const Invoice: FC<IInvoice> = ({ invoiceState, setInvoiceState }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { invoiced, isAutoSyncRecord } = state ?? {};

  const [sendCopy, setSendCopy] = useState(false);

  const invoiceDetails = useAppSelector(professionalInvoice);
  const generatedInvoice = useAppSelector(generatedProfessionalInvoice);
  const success = useAppSelector((st) =>
    successSelector([successAction], st));

  const { pdfContent } = generatedInvoice ?? {};

  const {
    professionalAddress,
    professionalCity,
    professionalName,
    professionalPhone,
    clientName,
    clientAddress,
    clientCity,
    professionalProvince,
    clientProvince,
    accountNumber,
    branchNumber,
    institutionNumber,
    professionalEmail,
    professionalPostalCode,
    clientEmail,
    clientPostalCode,
    bookings,
    date,
    invoiceNumber,
    tps,
    tvq,
  } = invoiceDetails ?? {};

  const [bookingsData, setBookingsData] = useState(bookings);

  const {
    meal,
    travel,
    accommodation,
    emergency,
    other,
    subTotal,
    totalRate,
    totalTPS,
    totalTVQ,
    grandTotal,
  } = useInvoicesCalculation(invoiceState);

  const table = {
    headers: tableHeader,
    rows: tableRow({ data: bookingsData, invoiceState }),
  };

  const professionalCompleteAddress = `${professionalAddress} ${professionalCity} ${professionalProvince}, ${professionalPostalCode}`;
  const pharmacyCompleteAddress = `${clientAddress} ${clientCity} ${clientProvince}${clientPostalCode ? ',' : ''} ${clientPostalCode}`;

  const goBack = () => {
    navigate(-1);
  };

  const handleChange = () => {
    setSendCopy((prev) =>
      !prev);
  };

  const handleRemoveBooking = (id:string) =>
    () => {
      setInvoiceState((prev) => {
        const { [id]: _, ...rest } = prev ?? {};
        return rest;
      });

      setBookingsData(bookingsData.filter((item) =>
        item.bookingId !== id));
    };

  const sendEmailToPharmacy = (sendToPharmacy?: boolean) =>
    () => {
      dispatch(
        generateProfessionalInvoice({
          isAutoSyncRecord,
          sendToPharmacy: !!sendToPharmacy,
          sendme: sendCopy,
          bookings: Object.values(invoiceState).map((booking) => {
            if (booking?.changed) {
              return {
                bookingId: booking.bookingId,
                startDate:
                booking.startDate.toFormat(MomentTimeFormats.fullDate) ?? '',
                endDate:
                booking.endDate.toFormat(MomentTimeFormats.fullDate) ?? '',
              };
            }
            return {
              bookingId: booking.bookingId,
            };
          }),
        }),
      );
    };

  const initialsArray = professionalName?.split?.(' ');
  const initial = initialsArray
    ?.slice(0, 2)
    .map((item) =>
      item[0])
    .join('');

  useEffect(() => {
    setBookingsData(bookings);
  }, [bookings]);

  useEffect(() => {
    if (success && !pdfContent) {
      dispatch(resetStatus([successAction]));
      showSuccess(dispatch, strings.invoiceSentToPharmacy);
    } else if (pdfContent && success) {
      downloadPDF(pdfContent, invoiceNumber);
      dispatch(resetStatus([successAction]));
      dispatch(resetProfessionalValue(['professionalInvoice']));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  const invoiceHeading = `${fr.invoice}/${en.invoice}`;

  return (
    <Box sx={styles.root}>
      <Box sx={styles.titleContainer}>
        <Box sx={styles.invoiceContainer}>
          <Typography sx={styles.invoiceTitle}>{invoiceHeading}</Typography>
          <Box sx={styles.pdfContainer}>
            <DownloadPDF onClick={sendEmailToPharmacy(false)} />
          </Box>
        </Box>
      </Box>
      <Box sx={styles.header}>
        <Box sx={styles.logo}>
          <Typography sx={styles.logoText}>{initial}</Typography>
        </Box>

        <Box sx={styles.flex}>
          <Box sx={[styles.margin, styles.dateBox]}>
            <Typography sx={styles.title}>{en.date}</Typography>
            <Typography sx={styles.infoText}>
              {getDayMonthDateYear(date)}
            </Typography>
          </Box>

          <Box sx={styles.dateBox}>
            <Typography sx={styles.title}>Invoice No</Typography>
            <Typography sx={styles.infoText}>{invoiceNumber}</Typography>
          </Box>
        </Box>
      </Box>

      <Grid sx={styles.flex} columnGap={2}>
        <InvoiceBox
          showLabel={false}
          title={`${fr.vendor}/${en.vendor}`}
          config={professionalConfig({
            professionalName,
            address: professionalCompleteAddress,
            email: professionalEmail,
            phone: professionalPhone,
          })}
        />
        <InvoiceBox
          showLabel={false}
          title={strings.clientBillTo}
          config={clientConfig({
            clientName,
            address: pharmacyCompleteAddress,
            email: clientEmail ?? '',
          })}
        />
      </Grid>

      <InvoiceTable
        handleRemoveBooking={handleRemoveBooking}
        table={table}
        setInvoiceState={setInvoiceState}
        invoiceState={invoiceState}
      />

      <Grid sx={styles.flex} columnGap={2}>
        <InvoiceBox
          show={!!accountNumber}
          note={`${fr.pleaseRemitDirectDeposit}/${en.pleaseRemitDirectDeposit}`}
          config={bankConfig({
            institutionNo: institutionNumber,
            branchNo: branchNumber,
            accountNo: accountNumber,
          })}
        />
        <InvoiceBox
          config={invoiceConfig({
            subTotal,
            totalRate,
            totalTPS,
            totalTVQ,
            meal,
            other,
            travel,
            accommodation,
            emergency,
            grandTotal,
            tps,
            tvq,
          })}
        />
      </Grid>

      <CustomCheckbox
        id="sendCopy"
        name="sendCopy"
        value={sendCopy}
        label={strings.sendCopyToMyEmail}
        handleChange={handleChange}
      />

      <Box sx={styles.buttonContainer}>
        <CustomButton
          label={strings.cancel}
          variant="outlined"
          customClass={styles.margin}
          onClick={goBack}
        />

        <CustomButton
          disabled={invoiced}
          label={strings.send}
          onClick={sendEmailToPharmacy(true)}
        />
      </Box>
    </Box>
  );
};

export default Invoice;
